import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Alert,
  Snackbar,
  Button,
  Chip,
  Fab,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Tooltip,
} from "@mui/material";
import {
  Edit as EditIcon,
  FileCopy as FileCopyIcon,
  Share as ShareIcon,
  Analytics as AnalyticsIcon,
  PlayArrow as UseIcon,
  ArrowBack as BackIcon,
  MoreVert as MoreIcon,
} from "@mui/icons-material";
import PageContainer from "../components/common/PageContainer";
import TemplateDetail from "../components/templates/TemplateDetail";
import TemplateForm from "../components/templates/TemplateForm";
import LoadingState from "../components/common/LoadingState";
import ErrorState from "../components/common/ErrorState";
import {
  updateTemplate,
  duplicateTemplate,
  getTemplateDisplayName,
  validateTemplate,
} from "../models/Template";
import { templateService } from "../services/templateService";

/**
 * Enhanced page component for viewing and editing templates with manager features
 */
const TemplateView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [template, setTemplate] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [speedDialOpen, setSpeedDialOpen] = useState(false);
  const [saveAction, setSaveAction] = useState("");

  useEffect(() => {
    if (id) {
      console.log(location.state);
      setTemplate(location.state);
      setLoading(false);
    }
  }, [id, location]);

  const handleEdit = () => {
    setIsEditing(true);
    setSaveAction("edit");
    setSpeedDialOpen(false);
  };

  const handleDuplicate = () => {
    navigate("/templates/new", { state: template });
  };

  const handleDelete = async (templateId) => {
    try {
      // In a real application, we would delete the template via the API
      console.log("Deleting template:", templateId);

      setSaveStatus({
        open: true,
        message: `Template "${getTemplateDisplayName(
          template
        )}" deleted successfully.`,
        severity: "success",
      });

      // Navigate back to templates page after a short delay
      setTimeout(() => {
        navigate("/templates");
      }, 1500);
    } catch (error) {
      setSaveStatus({
        open: true,
        message: "Failed to delete template. Please try again.",
        severity: "error",
      });
    }
  };

  // const handleDuplicate = async (templateId) => {
  //   try {
  //     if (template) {
  //       const duplicatedTemplate = duplicateTemplate(template, {
  //         name: `${template.name} (Copy)`,
  //         createdBy: "current_user_id", // This would come from auth context
  //       });

  //       console.log("Duplicating template:", duplicatedTemplate);

  //       setSaveStatus({
  //         open: true,
  //         message: `Template duplicated as "${duplicatedTemplate.name}".`,
  //         severity: "success",
  //       });

  //       // Navigate to edit the new template after a short delay
  //       setTimeout(() => {
  //         navigate(`/templates/${duplicatedTemplate.id}`);
  //       }, 1500);
  //     }
  //   } catch (error) {
  //     setSaveStatus({
  //       open: true,
  //       message: "Failed to duplicate template. Please try again.",
  //       severity: "error",
  //     });
  //   }
  //   setSpeedDialOpen(false);
  // };

  const handleUse = (templateId) => {
    // Navigate to create new checklist with this template
    navigate(`/checklists/new?templateId=${templateId}`);
    setSpeedDialOpen(false);
  };

  const handleShare = (templateId) => {
    // Implement sharing functionality
    console.log("Sharing template:", templateId);
    setSaveStatus({
      open: true,
      message: "Template sharing link copied to clipboard!",
      severity: "info",
    });
    setSpeedDialOpen(false);
  };

  const handleViewAnalytics = (templateId) => {
    // Navigate to analytics page
    console.log("Viewing analytics for template:", templateId);
    navigate(`/templates/${templateId}/analytics`);
    setSpeedDialOpen(false);
  };

  const handleToggleFavorite = (templateId) => {
    if (template) {
      const updatedTemplate = {
        ...template,
        isFavorite: !template.isFavorite,
      };
      setTemplate(updatedTemplate);

      setSaveStatus({
        open: true,
        message: `Template ${
          updatedTemplate.isFavorite ? "added to" : "removed from"
        } favorites.`,
        severity: "success",
      });
    }
  };

  const handleSaveEdit = async (templateData) => {
    try {
      // Validate the template data
      console.log("HandleSaveEdit - templateData: ", templateData);
      const validation = validateTemplate(templateData);

      if (!validation.isValid) {
        setSaveStatus({
          open: true,
          message: "Please fix validation errors before saving.",
          severity: "error",
        });
        return;
      }

      const templateToSave = {
        ...templateData,
        updatedBy: "current_user_id", // This would come from auth context
        // Ensure we have a country field for Lambda
        country: templateData.metadata?.countries?.[0] || "IT",
      };

      // Call the actual API service
      const editedTemplate = await templateService.editTemplate(
        templateToSave,
        id
      );

      // Update the template using the model
      // const updatedTemplate = updateTemplate(template, templateData);

      console.log("Saving updated template:", editedTemplate);

      // Update local state
      setTemplate(editedTemplate);
      setIsEditing(false);

      setSaveStatus({
        open: true,
        message: `Template "${editedTemplate.name}" updated successfully!`,
        severity: "success",
      });
      navigate("/templates");
    } catch (error) {
      console.error("Error saving template:", error);
      setSaveStatus({
        open: true,
        message: "Failed to save template. Please try again.",
        severity: "error",
      });
    }
  };

  const handleSaveDuplicate = async (templateData) => {
    try {
      // Validate the template data
      console.log("HandleSaveEdit - templateData: ", templateData);
      const validation = validateTemplate(templateData);

      if (!validation.isValid) {
        setSaveStatus({
          open: true,
          message: "Please fix validation errors before saving.",
          severity: "error",
        });
        return;
      }

      const templateToSave = {
        ...templateData,
        createdBy: "current_user_id", // This would come from auth context
        // Ensure we have a country field for Lambda
        country: templateData.metadata?.countries?.[0] || "IT",
      };

      // Call the actual API service
      const editedTemplate = await templateService.createTemplate(
        templateToSave
      );

      // Update the template using the model
      // const updatedTemplate = updateTemplate(template, templateData);

      console.log("Saving updated template:", editedTemplate);

      // Update local state
      setTemplate(editedTemplate);
      setIsEditing(false);

      setSaveStatus({
        open: true,
        message: `Template "${editedTemplate.name}" updated successfully!`,
        severity: "success",
      });
    } catch (error) {
      console.error("Error saving template:", error);
      setSaveStatus({
        open: true,
        message: "Failed to save template. Please try again.",
        severity: "error",
      });
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleCloseSnackbar = () => {
    setSaveStatus((prev) => ({ ...prev, open: false }));
  };

  const getBreadcrumbs = () => {
    const breadcrumbs = [{ text: "Templates", path: "/templates" }];

    if (template) {
      if (isEditing) {
        breadcrumbs.push(
          {
            text: getTemplateDisplayName(template),
            path: `/templates/${template.id}`,
          },
          { text: "Edit" }
        );
      } else {
        breadcrumbs.push({ text: getTemplateDisplayName(template) });
      }
    }

    return breadcrumbs;
  };

  const speedDialActions = [
    {
      icon: <UseIcon />,
      name: "Use Template",
      onClick: () => handleUse(template?.id),
    },
    {
      icon: <EditIcon />,
      name: "Edit Template",
      onClick: handleEdit,
    },
    {
      icon: <FileCopyIcon />,
      name: "Duplicate",
      onClick: () => handleDuplicate(template?.id),
    },
    {
      icon: <ShareIcon />,
      name: "Share",
      onClick: () => handleShare(template?.id),
    },
    {
      icon: <AnalyticsIcon />,
      name: "Analytics",
      onClick: () => handleViewAnalytics(template?.id),
    },
  ];

  if (loading) {
    return (
      <PageContainer title="Loading Template...">
        <LoadingState />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer title="Error">
        <ErrorState message={error} onRetry={() => window.location.reload()} />
      </PageContainer>
    );
  }

  if (!template) {
    return (
      <PageContainer title="Template Not Found">
        <ErrorState
          message="The requested template could not be found."
          onRetry={() => navigate("/templates")}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title={isEditing ? "Edit Template" : getTemplateDisplayName(template)}
      subtitle={
        isEditing
          ? "Make changes to your template"
          : `Version ${template.version} • ${template.items.length} items • ${template.metadata.estimatedTimeMinutes}min`
      }
      breadcrumbs={getBreadcrumbs()}
      actions={
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {/* Template Status */}
          {!template.isActive && (
            <Chip
              label="Inactive"
              color="error"
              variant="outlined"
              size="small"
            />
          )}

          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() => navigate("/templates")}
          >
            Back to Templates
          </Button>

          {/* {!isEditing && (
            <Button
              variant="contained"
              startIcon={<UseIcon />}
              onClick={() => handleUse(template.id)}
            >
              Use Template
            </Button>
          )} */}
        </Box>
      }
    >
      {isEditing ? (
        <TemplateForm
          template={template}
          onSave={saveAction === "edit" ? handleSaveEdit : handleSaveDuplicate}
          onCancel={handleCancel}
        />
      ) : (
        <Box sx={{ position: "relative" }}>
          <TemplateDetail
            template={template}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onDuplicate={handleDuplicate}
            onUse={handleUse}
            onToggleFavorite={handleToggleFavorite}
            onShare={handleShare}
            onViewAnalytics={handleViewAnalytics}
          />

          {/* Floating Speed Dial for Quick Actions */}
          <SpeedDial
            ariaLabel="Template actions"
            sx={{
              position: "fixed",
              bottom: 24,
              right: 24,
              "& .MuiFab-primary": {
                bgcolor: "primary.main",
                "&:hover": {
                  bgcolor: "primary.dark",
                },
              },
            }}
            icon={<SpeedDialIcon icon={<MoreIcon />} openIcon={<EditIcon />} />}
            onClose={() => setSpeedDialOpen(false)}
            onOpen={() => setSpeedDialOpen(true)}
            open={speedDialOpen}
          >
            {speedDialActions.map((action) => (
              <SpeedDialAction
                key={action.name}
                icon={action.icon}
                tooltipTitle={action.name}
                onClick={action.onClick}
              />
            ))}
          </SpeedDial>
        </Box>
      )}

      {/* Status Snackbar */}
      <Snackbar
        open={saveStatus.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={saveStatus.severity}
          variant="filled"
        >
          {saveStatus.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default TemplateView;
